import { useScope } from "~/hooks/useScope";
import {
  AggregateScoreOrder,
  AggregateScoreOrderField,
  OrderDirection,
  useGetAggregateScoresQuery,
} from "~/operations";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { usePaginate } from "~/components/pagination/usePaginate";
import { FINDINGS_TABLE_INITIAL_PAGE_RANGE } from "~/components/pagination";
import { useFirewatchFilterContext } from "~/components/FirewatchPage/hooks/useFirewatchFilterContext";

export function useInitiativeFindings({
  initiativeMrn,
}: {
  initiativeMrn: string;
}) {
  const { spaceScope } = useScope();
  const paginate = usePaginate({
    defaultPageSize: FINDINGS_TABLE_INITIAL_PAGE_RANGE.to,
  });
  const baseUrl = paginate.resetCursorUrl(location.href);

  const sort = useSort<AggregateScoreOrder>({
    baseUrl,
    defaultSort: {
      field: AggregateScoreOrderField.RiskValue,
      direction: OrderDirection.Desc,
    },
    validFields: [
      AggregateScoreOrderField.RiskValue,
      AggregateScoreOrderField.BlastRadius,
      AggregateScoreOrderField.Title,
    ],
  });

  const { filterForm, filterVariables, activeFilterCount, resetFilters } =
    useFirewatchFilterContext({
      scopeMrn: spaceScope?.mrn || "",
      pageType: "AllSecurityFindings",
      paginate,
      baseQueryFilters: {
        initiativeMrns: [initiativeMrn],
      },
    });

  const { data, error, loading, fetchMore } = useGetAggregateScoresQuery({
    variables: {
      entityMrn: spaceScope?.mrn || "",
      ...paginate.pageVars,
      orderBy: sort.orderBy,
      filter: {
        initiativeMrns: [initiativeMrn],
        ...filterVariables,
      },
    },
    skip: !spaceScope?.mrn || !initiativeMrn,
    fetchPolicy: "cache-and-network",
  });

  const scoresUnion = data?.aggregateScores;
  const scores =
    scoresUnion?.__typename === "AggregateScoresConnection"
      ? scoresUnion
      : undefined;
  const edges = scores?.edges || [];
  const totalCount = scores?.totalCount || 0;
  const pageInfo = scores?.pageInfo;

  return {
    loading,
    error,
    edges,
    totalCount,
    pageInfo,
    fetchMore,
    sort,
    paginate,
    filterForm,
    activeFilterCount,
    resetFilters,
  };
}
